/**
 * FocusRing — the shared KEYBOARD focus cursor: a soft accent ring drawn around whatever
 * card/tile the keyboard cursor currently sits on, so every grid screen has a visible,
 * non-hover affordance (the classic ▶ selection, reimagined as a clean ring for the card
 * grids). Lives at SCENE level (never nested in a ScrollList's content, which would be
 * destroyed on a grid rebuild), positioned by the owning scene in scene/world coords —
 * scenes auto-scroll their list so the focused item stays inside the viewport, so the
 * scene-level ring is always over a visible tile.
 *
 * Screen-simple + reduced-motion-friendly (a static stroke, no pulse). One per scene;
 * hidden until the player uses the keyboard, and re-drawn as the cursor moves.
 */
import Phaser from 'phaser';
import { COLORS, RADIUS } from './theme.js';

export interface FocusRingOptions {
  readonly depth?: number;
  readonly color?: number;
  /** Stroke thickness (px). */
  readonly thickness?: number;
}

export class FocusRing {
  private readonly gfx: Phaser.GameObjects.Graphics;
  private readonly color: number;
  private readonly thickness: number;

  constructor(scene: Phaser.Scene, options: FocusRingOptions = {}) {
    this.color = options.color ?? COLORS.accent;
    this.thickness = options.thickness ?? 3;
    this.gfx = scene.add
      .graphics()
      .setDepth(options.depth ?? 60)
      .setVisible(false);
  }

  /**
   * Stroke a rounded ring centered at (x, y) with the given card size. `pad` grows the ring
   * a little outside the card edge so it reads as a halo, not an inset border.
   */
  show(x: number, y: number, w: number, h: number, radius = RADIUS.md, pad = 3): void {
    const g = this.gfx;
    const rw = w + pad * 2;
    const rh = h + pad * 2;
    g.clear();
    g.lineStyle(this.thickness, this.color, 0.95);
    g.strokeRoundedRect(-rw / 2, -rh / 2, rw, rh, radius + pad);
    g.setPosition(x, y);
    g.setVisible(true);
  }

  hide(): void {
    this.gfx.clear();
    this.gfx.setVisible(false);
  }

  get visible(): boolean {
    return this.gfx.visible;
  }

  /** Raise above later-added objects (call after a grid rebuild adds fresh cards). */
  bringToTop(): void {
    this.gfx.setDepth(this.gfx.depth);
    if (this.gfx.scene) this.gfx.scene.children.bringToTop(this.gfx);
  }

  destroy(): void {
    this.gfx.destroy();
  }
}
